import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { HostAppShell } from '../components/HostAppShell';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  Zap, Check, Lock, ChevronRight, Megaphone, HeadphonesIcon,
  BarChart3, MapPin, Sparkles, ShieldPlus, ArrowRight, Loader2,
  CheckCircle, Percent, Clock, Eye, XCircle
} from 'lucide-react';
import { toast } from 'sonner';

// ============================================================================
// PLAN PRICING — Display-only constants, no billing logic
// ============================================================================
const PLAN_PRICING = { free: 0, basic: 19, pro: 29, premium: 49 };

// ============================================================================
// PLAN DEFINITIONS — Single source for comparison grid
// Fee modifiers match backend entitlements.py TIER_FEE_MODIFIERS
// ============================================================================
const PLANS = [
  {
    id: 'free',
    label: 'Free',
    tagline: 'Get started with HireHub',
    feeNote: 'Standard platform fees',
    feeDiscount: null,
    badgeColor: 'bg-slate-100 text-slate-600 border-slate-200',
    cardBorder: 'border-slate-200',
    headerBg: 'bg-slate-50',
    features: [],
  },
  {
    id: 'basic',
    label: 'Basic',
    tagline: 'Grow your bookings',
    feeNote: '5% lower platform fees',
    feeDiscount: '5%',
    badgeColor: 'bg-blue-100 text-blue-700 border-blue-200',
    cardBorder: 'border-blue-200',
    headerBg: 'bg-blue-50',
    features: ['boost_listing', 'priority_support'],
  },
  {
    id: 'pro',
    label: 'Pro',
    tagline: 'Professional fleet management',
    feeNote: '15% lower platform fees',
    feeDiscount: '15%',
    recommended: true,
    badgeColor: 'bg-violet-100 text-violet-700 border-violet-200',
    cardBorder: 'border-violet-300',
    headerBg: 'bg-violet-50',
    features: ['boost_listing', 'priority_support', 'advanced_analytics', 'gps_tracking'],
  },
  {
    id: 'premium',
    label: 'Premium',
    tagline: 'Everything, unlocked',
    feeNote: '30% lower platform fees',
    feeDiscount: '30%',
    badgeColor: 'bg-amber-100 text-amber-700 border-amber-200',
    cardBorder: 'border-amber-200',
    headerBg: 'bg-amber-50',
    features: ['boost_listing', 'priority_support', 'advanced_analytics', 'gps_tracking', 'ai_assistant', 'insurance_perks'],
  },
];

// Feature readiness: 'live' | 'coming_soon'
const ALL_FEATURES = [
  { key: 'boost_listing', label: 'Boost Listing', icon: Megaphone, desc: 'Higher visibility in search results', status: 'live' },
  { key: 'priority_support', label: 'Priority Support', icon: HeadphonesIcon, desc: 'Faster response from our team', status: 'live' },
  { key: 'advanced_analytics', label: 'Advanced Analytics', icon: BarChart3, desc: 'Deep fleet performance insights', status: 'live' },
  { key: 'gps_tracking', label: 'GPS Tracking', icon: MapPin, desc: 'Real-time trip tracking', status: 'live' },
  { key: 'ai_assistant', label: 'AI Assistant', icon: Sparkles, desc: 'AI pricing and fleet optimisation', status: 'live' },
  { key: 'insurance_perks', label: 'Insurance Perks', icon: ShieldPlus, desc: 'Enhanced coverage and claims support', status: 'coming_soon' },
];

const TIER_ORDER = ['free', 'basic', 'pro', 'premium'];

// Request status display config
const REQUEST_STATUS = {
  pending: { label: 'Request Pending', icon: Clock, color: 'bg-amber-50 text-amber-700 border-amber-200' },
  reviewing: { label: 'Under Review', icon: Eye, color: 'bg-blue-50 text-blue-700 border-blue-200' },
  approved: { label: 'Approved', icon: CheckCircle, color: 'bg-green-50 text-green-700 border-green-200' },
  declined: { label: 'Declined', icon: XCircle, color: 'bg-red-50 text-red-600 border-red-200' },
  completed: { label: 'Completed', icon: CheckCircle, color: 'bg-slate-50 text-slate-500 border-slate-200' },
};

// ============================================================================
// PLAN CARD — One per tier in the comparison grid
// ============================================================================
const PlanCard = ({ plan, isCurrent, isUpgrade, onRequestUpgrade, loading, requestStatus }) => {
  const isRecommended = plan.recommended && !isCurrent;
  const isActive = requestStatus === 'pending' || requestStatus === 'reviewing';
  const statusConfig = requestStatus ? REQUEST_STATUS[requestStatus] : null;
  const StatusIcon = statusConfig?.icon;
  return (
    <Card className={`relative overflow-hidden transition-all duration-200 ${plan.cardBorder} ${isCurrent ? 'ring-2 ring-[#004B8D] shadow-md' : isRecommended ? 'ring-2 ring-violet-300 shadow-md' : 'hover:shadow-sm'}`} data-testid={`plan-card-${plan.id}`}>
      {isCurrent && (
        <div className="absolute top-0 left-0 right-0 h-1 bg-[#004B8D]" />
      )}
      {isRecommended && !isCurrent && (
        <div className="absolute top-0 left-0 right-0 h-1 bg-violet-500" />
      )}
      <div className={`p-4 ${plan.headerBg}`}>
        <div className="flex items-center justify-between gap-2 mb-1">
          <h3 className="font-bold text-lg text-slate-900">{plan.label}</h3>
          <div className="flex items-center gap-1.5">
            {isRecommended && (
              <Badge className="bg-violet-600 text-white text-[10px] px-2 border-0" data-testid="recommended-badge">
                Most Popular
              </Badge>
            )}
            {isCurrent && (
              <Badge className="bg-[#004B8D] text-white text-[10px] px-2">Current</Badge>
            )}
          </div>
        </div>
        <div className="flex items-baseline gap-1 mt-1" data-testid={`plan-price-${plan.id}`}>
          <span className="text-2xl font-bold text-slate-900">${PLAN_PRICING[plan.id] ?? 0}</span>
          <span className="text-sm text-slate-400">/ month</span>
        </div>
        <p className="text-sm text-slate-500 mt-1">{plan.tagline}</p>
        {plan.feeDiscount && (
          <div className="flex items-center gap-1.5 mt-2">
            <Percent className="w-3.5 h-3.5 text-green-600" />
            <span className="text-xs font-semibold text-green-700">{plan.feeNote}</span>
          </div>
        )}
        {!plan.feeDiscount && (
          <p className="text-xs text-slate-400 mt-2">{plan.feeNote}</p>
        )}
      </div>
      <CardContent className="p-4 space-y-1.5">
        {ALL_FEATURES.map(f => {
          const included = plan.features.includes(f.key);
          const isComingSoon = f.status === 'coming_soon';
          return (
            <div key={f.key} className={`flex items-start gap-2.5 py-1 ${included ? 'text-slate-700' : 'text-slate-300'}`}>
              {included ? (
                <Check className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
              ) : (
                <Lock className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
              )}
              <div className="min-w-0">
                <span className="text-sm font-medium inline-flex items-center gap-1.5">
                  {f.label}
                  {included && isComingSoon && (
                    <span className="text-[10px] font-medium text-amber-600 bg-amber-50 border border-amber-200 px-1.5 py-0 rounded" data-testid={`coming-soon-${f.key}`}>Soon</span>
                  )}
                </span>
                {included && (
                  <p className="text-[11px] text-slate-400 leading-tight">{f.desc}</p>
                )}
              </div>
            </div>
          );
        })}
        <div className="pt-3">
          {isCurrent ? (
            <div className="text-center py-2 text-sm text-slate-400 font-medium" data-testid="current-plan-label">
              Your current plan
            </div>
          ) : isUpgrade && isActive && statusConfig ? (
            <div className={`flex items-center justify-center gap-2 py-2.5 rounded-lg border text-sm font-medium ${statusConfig.color}`} data-testid={`request-status-${plan.id}`}>
              {StatusIcon && <StatusIcon className="w-4 h-4" />}
              {statusConfig.label}
            </div>
          ) : isUpgrade && requestStatus === 'approved' ? (
            <div className={`flex items-center justify-center gap-2 py-2.5 rounded-lg border text-sm font-medium ${REQUEST_STATUS.approved.color}`} data-testid={`request-status-${plan.id}`}>
              <CheckCircle className="w-4 h-4" />
              Approved
            </div>
          ) : isUpgrade ? (
            <Button 
              className={`w-full h-10 text-sm ${isRecommended ? 'bg-violet-600 hover:bg-violet-700' : 'bg-[#004B8D] hover:bg-[#003a6d]'}`}
              onClick={() => onRequestUpgrade(plan.id)}
              disabled={loading}
              data-testid={`request-upgrade-${plan.id}`}
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <ArrowRight className="w-4 h-4 mr-2" />}
              Upgrade to {plan.label}
            </Button>
          ) : (
            <div className="text-center py-2 text-xs text-slate-300 font-medium">
              Included in your plan
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

// ============================================================================
// MAIN PAGE
// ============================================================================
const HostManagePlanPage = () => {
  const { user, api, getSubscriptionTier, hasFeature, refreshUser } = useAuth();
  const [requestingTier, setRequestingTier] = useState(null);
  const [myRequests, setMyRequests] = useState([]);
  const [tierRefreshed, setTierRefreshed] = useState(false);
  const currentTier = getSubscriptionTier();
  const currentIdx = TIER_ORDER.indexOf(currentTier);

  // Refresh user entitlements on mount to pick up PO-applied tier changes
  useEffect(() => {
    refreshUser().then(() => setTierRefreshed(true)).catch(() => setTierRefreshed(true));
  }, [refreshUser]);

  // Fetch host's own upgrade requests on mount
  useEffect(() => {
    const fetchRequests = async () => {
      try {
        const res = await api.get('/host/upgrade-requests');
        setMyRequests(res.data.requests || []);
      } catch {
        // Silently fail — page still works without request status
      }
    };
    fetchRequests();
  }, [api]);

  // Build a map: tier → most recent request status
  // KEY RULE: Suppress stale request statuses for tiers at or below the host's current tier
  const tierRequestStatus = {};
  for (const req of myRequests) {
    const tier = req.requested_tier;
    const tierIdx = TIER_ORDER.indexOf(tier);
    // Skip if the host's current tier already meets or exceeds this requested tier
    if (tierIdx <= currentIdx) continue;
    // Only track the most recent request per tier (list is sorted by created_at desc)
    if (!tierRequestStatus[tier]) {
      tierRequestStatus[tier] = req.status;
    }
  }

  const handleRequestUpgrade = async (targetTier) => {
    setRequestingTier(targetTier);
    try {
      const res = await api.post('/host/upgrade-request', { 
        requested_tier: targetTier,
        current_tier: currentTier 
      });
      if (res.data.status === 'duplicate') {
        toast.info(res.data.message);
      } else {
        toast.success(`Upgrade interest registered for ${targetTier.charAt(0).toUpperCase() + targetTier.slice(1)} plan. We'll be in touch!`);
      }
      // Re-fetch to update status display
      const updated = await api.get('/host/upgrade-requests');
      setMyRequests(updated.data.requests || []);
    } catch (err) {
      toast.error('Could not submit request. Please try again.');
    } finally {
      setRequestingTier(null);
    }
  };

  // Compute banner states with stale-suppression
  // Only show active requests for tiers ABOVE the current tier
  const activeRequests = myRequests.filter(r => 
    (r.status === 'pending' || r.status === 'reviewing') && 
    TIER_ORDER.indexOf(r.requested_tier) > currentIdx
  );
  // Check if any approved request's tier is now the current tier (activation complete)
  const activatedRequest = currentTier !== 'free' ? myRequests.find(r => 
    (r.status === 'approved' || r.status === 'completed') && 
    TIER_ORDER.indexOf(r.requested_tier) <= currentIdx
  ) : null;
  // Show approved banner only for tiers NOT yet activated
  const pendingApproved = myRequests.find(r => 
    r.status === 'approved' && TIER_ORDER.indexOf(r.requested_tier) > currentIdx
  );
  // Declined: only show for tiers above current (stale-suppressed)
  const recentDeclined = myRequests.find(r => 
    r.status === 'declined' && TIER_ORDER.indexOf(r.requested_tier) > currentIdx
  );

  return (
    <HostAppShell title="Manage Plan" activeId="plan">
      <div className="max-w-5xl mx-auto space-y-8">
        
        {/* Current Plan Hero */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4" data-testid="manage-plan-header">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Plans & Features</h1>
            <p className="text-sm text-slate-500 mt-1">Choose the plan that matches how actively you host.</p>
          </div>
          <div className="flex items-center gap-2">
            <Zap className="w-4 h-4 text-slate-500" />
            <span className="text-sm text-slate-600">Current plan:</span>
            <Badge className={`text-xs font-bold border ${(PLANS.find(p => p.id === currentTier) || PLANS[0]).badgeColor}`} data-testid="current-tier-display">
              {(PLANS.find(p => p.id === currentTier) || PLANS[0]).label}
            </Badge>
          </div>
        </div>

        {/* Activation Success Banner — shown when tier is now active from a recent approval */}
        {activatedRequest && (
          <div className="flex items-center gap-3 p-3 bg-green-50 border border-green-200 rounded-xl" data-testid="activation-success-banner">
            <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
            <p className="text-sm text-green-700">
              Your <span className="font-semibold">{(PLANS.find(p => p.id === currentTier) || PLANS[0]).label}</span> plan is now active. New tools are available in your dashboard.
            </p>
          </div>
        )}

        {/* Active Request Status Banner — only for unresolved requests above current tier */}
        {!activatedRequest && activeRequests.length > 0 && (
          <div className="flex items-center gap-3 p-3 bg-amber-50 border border-amber-200 rounded-xl" data-testid="active-request-banner">
            <Clock className="w-4 h-4 text-amber-600 flex-shrink-0" />
            <p className="text-sm text-amber-700">
              You have {activeRequests.length} upgrade {activeRequests.length === 1 ? 'request' : 'requests'} being reviewed. We'll update you once a decision is made.
            </p>
          </div>
        )}
        {!activatedRequest && !activeRequests.length && pendingApproved && (
          <div className="flex items-center gap-3 p-3 bg-green-50 border border-green-200 rounded-xl" data-testid="approved-request-banner">
            <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
            <p className="text-sm text-green-700">
              Your upgrade to <span className="font-semibold">{pendingApproved.requested_tier.charAt(0).toUpperCase() + pendingApproved.requested_tier.slice(1)}</span> has been approved. Your tier will be updated shortly.
            </p>
          </div>
        )}
        {!activatedRequest && !activeRequests.length && !pendingApproved && recentDeclined && (
          <div className="flex items-center gap-3 p-3 bg-red-50 border border-red-100 rounded-xl" data-testid="declined-request-banner">
            <XCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
            <p className="text-sm text-red-600">
              Your request for <span className="font-semibold">{recentDeclined.requested_tier.charAt(0).toUpperCase() + recentDeclined.requested_tier.slice(1)}</span> was declined. You can submit a new request or contact support.
            </p>
          </div>
        )}

        {/* Tier Comparison Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="plan-comparison-grid">
          {PLANS.map((plan) => {
            const planIdx = TIER_ORDER.indexOf(plan.id);
            const isCurrent = plan.id === currentTier;
            const isUpgrade = planIdx > currentIdx;
            return (
              <PlanCard
                key={plan.id}
                plan={plan}
                isCurrent={isCurrent}
                isUpgrade={isUpgrade}
                onRequestUpgrade={handleRequestUpgrade}
                loading={requestingTier === plan.id}
                requestStatus={isUpgrade ? tierRequestStatus[plan.id] : null}
              />
            );
          })}
        </div>

        {/* Your Unlocked Features Detail */}
        <Card className="border-slate-200" data-testid="feature-detail-section">
          <CardContent className="p-5">
            <h2 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-green-500" />
              Your Feature Access
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {ALL_FEATURES.map(f => {
                const unlocked = hasFeature(f.key);
                const requiredPlan = PLANS.find(p => p.features.includes(f.key) && TIER_ORDER.indexOf(p.id) === Math.min(...PLANS.filter(pp => pp.features.includes(f.key)).map(pp => TIER_ORDER.indexOf(pp.id))));
                const FeatureIcon = f.icon;
                return (
                  <div 
                    key={f.key} 
                    className={`flex items-start gap-3 p-3 rounded-xl border transition-all ${
                      unlocked 
                        ? 'bg-green-50/50 border-green-100' 
                        : 'bg-slate-50 border-slate-100'
                    }`}
                    data-testid={`feature-detail-${f.key}`}
                  >
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                      unlocked ? 'bg-green-100' : 'bg-slate-100'
                    }`}>
                      <FeatureIcon className={`w-4 h-4 ${unlocked ? 'text-green-600' : 'text-slate-400'}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`text-sm font-medium ${unlocked ? 'text-slate-800' : 'text-slate-500'}`}>{f.label}</span>
                        {unlocked ? (
                          <Check className="w-3.5 h-3.5 text-green-500" />
                        ) : (
                          <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-4 text-slate-400 border-slate-200">
                            {requiredPlan?.label || 'Upgrade'}
                          </Badge>
                        )}
                        {f.status === 'coming_soon' && (
                          <span className="text-[10px] font-medium text-amber-600 bg-amber-50 border border-amber-200 px-1.5 py-0 rounded">Coming Soon</span>
                        )}
                      </div>
                      <p className="text-xs text-slate-400 mt-0.5">{f.desc}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Contact CTA + Billing Note */}
        <div className="text-center py-4 space-y-2" data-testid="billing-note">
          <p className="text-xs text-slate-400">
            Plans are billed monthly once subscriptions are enabled. For now, upgrades are manually reviewed.
          </p>
          <p className="text-sm text-slate-400">
            Have questions about plans? <a href="mailto:hirehubaustralia@gmail.com" className="text-[#004B8D] underline underline-offset-2 hover:text-[#003a6d]">Contact our team</a>
          </p>
        </div>
      </div>
    </HostAppShell>
  );
};

export default HostManagePlanPage;
